import { RedoListingStatus } from "../inventory/redo-listing-status";
import { SortDirection, TableSort } from "../tables/table";
import { ArrayStringFilterOperator } from "./advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "./advanced-filters/boolean-filter";
import { AdvancedFilterType } from "./advanced-filters/generic-advanced-filter-data";
import { AdvancedFilterData, DEFAULT_PAGE_SIZE, viewTypes } from "./views";

// Browse views default to hiding archived listings. Seeded as a default filter
// so the user can override it (edit/clear the Status filter) and their choice
// wins.
const hideArchivedFilter: AdvancedFilterData = {
  name: "status",
  type: AdvancedFilterType.ARRAY_TO_STRING,
  value: [RedoListingStatus.ARCHIVED],
  operator: ArrayStringFilterOperator.NONE_OF,
};

export enum InventoryLevelsSystemView {
  ALL = "all",
  FORECASTING = "forecasting",
}

export const sortDefault: TableSort = {
  direction: SortDirection.ASC,
  key: "title",
};

const defaultColumns = [
  { key: "selected", width: 40, hidden: false },
  { key: "title", width: 350, hidden: false },
  { key: "locations", width: 150, hidden: false },
  { key: "vendor", width: 150, hidden: false },
  { key: "cost", width: 100, hidden: false },
  { key: "merchant", width: 180, hidden: true },
  { key: "totalAvailable", width: 120, hidden: false },
  { key: "totalQuantity", width: 120, hidden: false },
  { key: "totalCommitted", width: 120, hidden: false },
  { key: "incomingShipments", width: 150, hidden: false },
];

const forecastingColumns = [
  { key: "selected", width: 40, hidden: false },
  { key: "title", width: 300, hidden: false, pinned: true },
  { key: "locations", width: 150, hidden: false },
  { key: "forecastingEnabled", width: 130, hidden: false },
  { key: "daysOfStockRemaining", width: 150, hidden: false },
  { key: "daysUntilStockout", width: 150, hidden: true },
  { key: "stockoutDate", width: 150, hidden: true },
  { key: "reorderThreshold", width: 160, hidden: false },
  { key: "daysUntilReorder", width: 150, hidden: false },
  { key: "unitsToOrder", width: 150, hidden: false },
  { key: "totalQuantity", width: 150, hidden: false },
  { key: "incomingShipments", width: 150, hidden: false },
  { key: "moq", width: 100, hidden: true },
  { key: "leadTime", width: 150, hidden: false },
  { key: "buffer", width: 150, hidden: false },
  { key: "inventoryValue", width: 150, hidden: false },
  { key: "supplier", width: 150, hidden: false },
  { key: "vendor", width: 150, hidden: false },
  { key: "sold30Days", width: 130, hidden: false },
  { key: "sold60Days", width: 130, hidden: false },
  { key: "sold90Days", width: 130, hidden: false },
  { key: "sold180Days", width: 130, hidden: true },
  { key: "sold365Days", width: 130, hidden: true },
  { key: "sold7Days", width: 130, hidden: true },
  { key: "sold14Days", width: 130, hidden: true },
  { key: "sold21Days", width: 130, hidden: true },
  { key: "sold45Days", width: 130, hidden: true },
  { key: "sold120Days", width: 130, hidden: true },
  { key: "forecast30Days", width: 150, hidden: false },
  { key: "forecast60Days", width: 150, hidden: false },
  { key: "forecast90Days", width: 150, hidden: false },
  { key: "forecast180Days", width: 150, hidden: true },
  { key: "forecast365Days", width: 150, hidden: true },
  { key: "forecast7Days", width: 150, hidden: true },
  { key: "forecast14Days", width: 150, hidden: true },
  { key: "forecast21Days", width: 150, hidden: true },
  { key: "forecast45Days", width: 150, hidden: true },
  { key: "forecast120Days", width: 150, hidden: true },
  { key: "totalAvailable", width: 120, hidden: true },
  { key: "totalCommitted", width: 120, hidden: true },
];

export const StaticSystemViews: Record<
  InventoryLevelsSystemView,
  viewTypes["inventory-levels"]
> = {
  [InventoryLevelsSystemView.ALL]: {
    table: "inventory-levels",
    name: InventoryLevelsSystemView.ALL,
    filters: [hideArchivedFilter],
    columns: defaultColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 0,
    isNotEditable: true,
  },
  [InventoryLevelsSystemView.FORECASTING]: {
    table: "inventory-levels",
    name: InventoryLevelsSystemView.FORECASTING,
    filters: [
      {
        name: "forecastingEnabled",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      },
      hideArchivedFilter,
    ],
    columns: forecastingColumns,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: sortDefault,
    position: 1,
    isNotEditable: true,
  },
};

export function friendlyViewName(view: viewTypes["inventory-levels"]) {
  switch (view.name) {
    case InventoryLevelsSystemView.ALL:
      return "All";
    case InventoryLevelsSystemView.FORECASTING:
      return "Forecasting";
    default:
      return view.name;
  }
}
